import { useState, useEffect, useMemo, useRef } from 'react';
import { getExpbDocForSite } from '../api/expbDoc';
import { EXPB_DOC_CONSTANTS } from '../core/constants';
import { initialExpbSiteConfig } from '../utilities/initialConfigs';
const { CONFIG_EDIT_MODES } = EXPB_DOC_CONSTANTS;

const useExpbDoc = (siteId, user) => {
	const [expbDoc, setExpbDoc] = useState(null);
	const [expbDocEditMode, setExpbDocEditMode] = useState('');
	const previousExpbDoc = useRef({});

	/**
	 * @name getAllEmailsPresentInConfig
	 * @description This function returns all available emails present in the expb site config
	 * @returns {string[]}
	 */
	const getAllEmailsPresentInConfig = () => {
		let allEmails = [];
		if (expbDoc) {
			const additionalSiteAccessEmails = expbDoc.additionalSiteAccessEmails || [];
			const experimentOwnerEmails = expbDoc.experiments.map((experiment) => {
				return experiment.email;
			});
			allEmails = [...additionalSiteAccessEmails, ...experimentOwnerEmails];
		}
		return allEmails;
	};

	const allAccessEmails = useMemo(getAllEmailsPresentInConfig, [expbDoc]);
	const userHasExpbDocEditAccess =
		(user && allAccessEmails.includes(user.email)) ||
		user.isSuperAdmin ||
		expbDocEditMode === CONFIG_EDIT_MODES.CREATE;

	/**
	 * @name fetchAndInitializeExpbDocAndEditMode
	 * @description This function is used to initialize expb doc for site and its edit mode
	 * @param {Function} expbDocForSiteReq
	 */
	async function fetchAndInitializeExpbDocAndEditMode(expbDocForSiteReq) {
		if (siteId && !expbDoc) {
			try {
				const { data } = await expbDocForSiteReq.promise(siteId);
				setExpbDoc(data.data.expbDocConfigForSite);
				previousExpbDoc.current = data.data.expbDocConfigForSite;
				setExpbDocEditMode(CONFIG_EDIT_MODES.UPDATE);
			} catch (err) {
				console.log('Error is : ', err);
				if (err?.response?.status === 404) {
					setExpbDoc(initialExpbSiteConfig);
					previousExpbDoc.current = initialExpbSiteConfig;
					setExpbDocEditMode(CONFIG_EDIT_MODES.CREATE);
				} else {
					setExpbDocEditMode(CONFIG_EDIT_MODES.SERVER_ERROR);
				}
			}
		}
	}

	useEffect(() => {
		let expbDocForSiteReq = getExpbDocForSite(siteId);
		fetchAndInitializeExpbDocAndEditMode(expbDocForSiteReq);
		return () => {
			expbDocForSiteReq.abort();
		};
	}, [siteId]);

	return {
		expbDoc,
		updateExpbDoc: setExpbDoc,
		expbDocEditMode,
		userHasExpbDocEditAccess,
		previousExpbDoc,
		allAccessEmails,
		updateExpbEditMode: setExpbDocEditMode
	};
};

export default useExpbDoc;
